'use client';

import { useId } from 'react';
import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiFileList3Line, RiLockLine, RiScales3Line, RiShieldCheckLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { dur, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * "Nasıl çalışıyoruz" — dört madde, gri zeminde ölçüm ızgarası.
 *
 * Anlatının (story) hemen ALTINDA olmak zorunda: o bölüm "bu ürünü niye
 * yaptık" diyor ve tam o cümlenin arkasından "peki bunu her gün nasıl
 * tutuyorsunuz" sorusu geliyor. Burası o sorunun cevabı. Araya bir bölüm
 * girerse cevap sorudan kopar.
 *
 * Zemin --gray-50: üstündeki anlatı ve altındaki kapanış CTA'sının zemini
 * beyaz; üçü de beyaz olsaydı sayfanın sonu tek uzun gövde gibi akardı.
 * Anasayfadaki beyaz–gri–beyaz nefesinin aynısı.
 *
 * ── Maddeler uydurma DEĞİL ────────────────────────────────────────────────
 *
 * Dördünün de üründe karşılığı var ve metinler oradaki davranışı anlatıyor:
 * onay modunun varsayılan olması ve autopilot'un elle açılması, denetim
 * kaydının tuttuğu alanlar, bütçe/aksiyon sınırlarının çalışma alanı ayarı
 * olması, OAuth token'larının şifreli saklanması. Sayfanın YER TUTUCU olan
 * kısımları künye ve anlatı; bu blok değil (messages → `about._DEMO`).
 * Metni değiştirirken ürünün gerçekten yaptığı şeyle çeliştirme.
 *
 * ── Çizgi dili ────────────────────────────────────────────────────────────
 *
 * Platformlar bölümüyle aynı: hücreler arasında boşluk YOK, ayracı hücrenin
 * kendi 1px'lik hattı çiziyor. Boşluk verilseydi üst hatlar parçalara bölünür,
 * ızgaranın tepesindeki kesintisiz çizgi kaybolurdu.
 *
 * Hatların hepsi ayrı öğe, `border` değil: border animasyonlanamaz, transform
 * animasyonlanır (DESIGN.md §1). Yön hareketin anlamını taşıyor — yatay hat
 * SOLDAN çekiliyor, dikey ayraç YUKARIDAN iniyor; sayfanın geri kalanıyla
 * aynı yön.
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * Sıra: önce ızgara kuruluyor (hat → ayraç), sonra içerik yerleşiyor
 * (ikon → başlık → metin). Yani "kutu çiziliyor, sonra doluyor".
 *
 * Hücreler arası gecikme `stagger.base`; dört hücre × 0.08 = 0.32 sn, §5'teki
 * 1.2 sn sınırının çok altında. Blur yalnızca dört metinde — ikonlar ve
 * başlıklar keskin giriyor.
 *
 * Döngü ve parallax yok.
 *
 * Giriş hareketi YOK (kullanıcı kararı, 9 Eyl 2026): bölüm sayfa açılır
 * açılmaz son hâlinde basılıyor (`initial={false}`). Eskiden `whileInView` +
 * `once: true` ile kaydırdıkça açılıyordu; site sahipleri sayfanın yüklenmiş
 * gelmesini istedi. Variant'lar yerinde — geri istenirse tetik tek satır.
 */

type Principle = {
  key: string;
  icon: ReactNode;
};

/* Sıra ağırlık sırası: önce izin (hiçbir şey olmadan önceki koşul), sonra iz
   (olduktan sonraki kanıt), sonra sınır (ne kadarı olabilir), en sonda erişim
   (her şeyi kesme hakkı). İkonlar kutu İÇİNDE değil — DESIGN.md §3. */
const ITEMS: readonly Principle[] = [
  { key: 'consent', icon: <RiShieldCheckLine size={24} aria-hidden="true" /> },
  { key: 'trace', icon: <RiFileList3Line size={24} aria-hidden="true" /> },
  { key: 'limits', icon: <RiScales3Line size={24} aria-hidden="true" /> },
  { key: 'data', icon: <RiLockLine size={24} aria-hidden="true" /> },
];

const sectionV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

/* Başlık bloğu tek parça girer — etiket, başlık ve giriş bölünmez. */
const headV: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const gridV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: stagger.tight } },
};

/* Hücrenin kendisi bir şey animasyonlamıyor; çocuklarının sırasını tutuyor. */
const cellV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight } },
};

const ruleV: Variants = {
  hidden: { opacity: 0, scaleX: 0 },
  show: { opacity: 1, scaleX: 1, transition: springFade(spring.default) },
};

const edgeV: Variants = {
  hidden: { opacity: 0, scaleY: 0 },
  show: { opacity: 1, scaleY: 1, transition: springFade(spring.default) },
};

const iconV: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: springFade(spring.snap) },
};

const titleV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const textV: Variants = {
  hidden: { opacity: 0, y: 8, filter: 'blur(5px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: springFade(spring.default, dur.base),
  },
};

export function AboutPrinciples() {
  const t = useTranslations('about.principles');
  const uid = useId();

  return (
    <section className="tenet" aria-labelledby={`${uid}-title`}>
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div className="tenet-frame" variants={sectionV} initial={false} animate="show">
            {/* Başlık bloğu ve ızgara farklı yatay dolgu taşıyor, bilinçli:
                hücrelerin üst hatları çerçevenin dikey hatlarına kadar
                uzansın diye ızgara dolgusuz — 30px içeride biten bir hat
                panelin tepesini kapatmaz, havada asılı kalır. */}
            <motion.div className="tenet-head" variants={headV}>
              <p className="tenet-eyebrow">{t('eyebrow')}</p>
              <h2 className="tenet-title" id={`${uid}-title`}>
                {t.rich('title', { hl })}
              </h2>
              <p className="tenet-lead">{t('lead')}</p>
            </motion.div>

            <motion.ul className="tenet-grid" variants={gridV}>
              {ITEMS.map((item) => (
                <motion.li className="tenet-cell" key={item.key} variants={cellV}>
                  <motion.span className="tenet-rule" aria-hidden="true" variants={ruleV} />
                  {/* Dikey ayraç her hücrede basılıyor ama yalnızca iki
                      sütunlu yerleşimde ve yalnızca SAĞ sütunda görünüyor
                      (globals.css). Basılıp CSS ile gizlenmesi bilinçli:
                      kırılma noktasını JS'te bilmek gerekmiyor, yerleşim
                      değişince kural kendiliğinden doğru hücreyi seçiyor. */}
                  <motion.span className="tenet-edge" aria-hidden="true" variants={edgeV} />

                  <motion.span className="tenet-icon" variants={iconV}>
                    {item.icon}
                  </motion.span>

                  <motion.h3 className="tenet-name" variants={titleV}>
                    {t(`items.${item.key}.title`)}
                  </motion.h3>

                  <motion.p className="tenet-text" variants={textV}>
                    {t(`items.${item.key}.text`)}
                  </motion.p>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
